import React, {
  createContext, useContext, useMemo, useState,
} from 'react';
import {
  Box, Flex, Heading, Text, useColorModeValue,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiMove } from 'react-icons/fi';
import { FaCheck } from 'react-icons/fa';

// item 364 — user-arrangeable page sections (Chad: "choose what's at the top").
// Each section is wrapped in <Orderable id> inside an <OrderableProvider>; moving uses
// CSS flex `order` (the DOM never restructures, so a card's own state — search text,
// scroll position, focus — survives a reorder), position persists per device in
// localStorage. Built for the Accountant page first; drop the same wrapper on any page.
// item 478 — sections can also belong to a collapsible GROUP: pass `group` on every member
// and `groupTitle` on the FIRST member; the group header collapses/expands the whole group
// (per-device). So the accountant page reads as a tidy hierarchy, not a wall of cards.
// item 604 — reordering is now GRAB-A-HANDLE native HTML5 drag-and-drop (replaced the
// per-section up/down arrows). Drag STARTS ONLY from the little grip handle: the card body
// stays fully interactive (a ledger's search box / scroll / row-clicks, a chat, etc. never
// get hijacked). No dnd library — CRA4/webpack4/Node16 rules that out; this is hand-rolled.
// The same engine drives the Universe dashboard via `DraggableCards` (a thin wrapper).

// hover background for the grip handle (a plain constant so the sx reference is stable and
// no hook is needed in a path that also has early returns)
const GRIP_HOVER_BG = 'rgba(127,127,127,0.18)';

interface DropTarget { id: string; before: boolean }

interface OrderCtx {
  posOf: (id: string) => number;
  isCollapsed: (group: string) => boolean;
  toggleGroup: (group: string) => void;
  expandGroup: (group: string) => void;
  // item 496 — one master control for every KNOWN group (the `groups` prop below)
  allGroupsOpen: boolean;
  toggleAllGroups: () => void;
  // item 604 — drag state + reorder
  draggingId: string | null;
  dropTarget: DropTarget | null;
  beginDrag: (id: string) => void;
  hoverCard: (id: string, before: boolean) => void;
  endDrag: () => void;
  reorder: (draggedId: string, targetId: string, before: boolean) => void;
}

const Ctx = createContext<OrderCtx | null>(null);

// item 485 — lets a page react to a deep-link (e.g. a notification landing on a card).
export const useOrderable = () => useContext(Ctx);

const readSaved = (storageKey: string): string[] => {
  try {
    const raw = JSON.parse(window.localStorage.getItem(storageKey) ?? '[]');
    return Array.isArray(raw) ? raw.filter((k) => typeof k === 'string') : [];
  } catch {
    return [];
  }
};

export const OrderableProvider: React.FC<{
  storageKey: string;
  ids: string[];
  // item 496 — every known group name on this page, so "expand all / collapse all" has a
  // complete list to act on (groups are otherwise declared ad hoc wherever they're used)
  groups?: string[];
  // item 685 (Chad) — when true, opening a group collapses the others (single-open). Opt-in per page.
  autoCollapse?: boolean;
  // item 1048 (Chad) — groups collapsed by DEFAULT (the other role's desk) — used only when
  // this device has never saved a collapse state; the user's own toggles always win after.
  defaultCollapsed?: string[];
  children: React.ReactNode;
}> = ({
  storageKey, ids, groups, autoCollapse, defaultCollapsed, children,
}) => {
  const [saved, setSaved] = useState<string[]>(() => readSaved(storageKey));
  const collapseKey = `${storageKey}:collapsedGroups`;
  const [collapsed, setCollapsed] = useState<string[]>(() => {
    // a null raw value = never saved on this device → seed from defaultCollapsed
    try {
      if (window.localStorage.getItem(collapseKey) === null) return defaultCollapsed ?? [];
    } catch { /* no storage */ }
    return readSaved(collapseKey);
  });
  // item 604 — transient drag state (not persisted). Living in context is fine: the card
  // BODIES are passed as `children` (stable element refs), so a dragover re-render of the
  // provider reconciles to the same subtree and never re-renders the card contents.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  // `groups` array literals are re-created every render by callers (e.g. an inline JSX prop),
  // so depending on `groups` directly would defeat the point: the `value` memo below depends on
  // `knownGroups` and would then also recompute every render. `groupsKey` is a stable primitive
  // that only changes when the group NAMES actually change.
  const groupsKey = (groups ?? []).join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const knownGroups = useMemo(() => groups ?? [], [groupsKey]);

  // effective order = the saved arrangement (existing ids only) + any new sections in
  // their natural page order — so shipping a new card never scrambles a saved layout
  const order = useMemo(() => {
    const known = saved.filter((k) => ids.includes(k));
    return [...known, ...ids.filter((k) => !known.includes(k))];
  }, [saved, ids]);

  const value = useMemo<OrderCtx>(() => ({
    posOf: (id) => order.indexOf(id),
    isCollapsed: (group) => collapsed.includes(group),
    toggleGroup: (group) => {
      const isCollapsed = collapsed.includes(group);
      // item 685 (Chad) — auto-collapse: OPENING a group collapses every other known group (single-open);
      // collapsing behaves normally.
      let next: string[];
      if (autoCollapse && isCollapsed) {
        next = knownGroups.filter((g) => g !== group);
      } else {
        next = isCollapsed ? collapsed.filter((g) => g !== group) : [...collapsed, group];
      }
      try { window.localStorage.setItem(collapseKey, JSON.stringify(next)); } catch { /* no storage */ }
      setCollapsed(next);
    },
    // item 485 — force a group open (used when a deep-link must reveal a collapsed card)
    expandGroup: (group) => {
      if (!collapsed.includes(group)) return;
      const next = collapsed.filter((g) => g !== group);
      try { window.localStorage.setItem(collapseKey, JSON.stringify(next)); } catch { /* no storage */ }
      setCollapsed(next);
    },
    // item 496 — expand/collapse every known group at once
    allGroupsOpen: knownGroups.length > 0 && knownGroups.every((g) => !collapsed.includes(g)),
    toggleAllGroups: () => {
      const allOpen = knownGroups.length > 0 && knownGroups.every((g) => !collapsed.includes(g));
      const next = allOpen ? knownGroups : [];
      try { window.localStorage.setItem(collapseKey, JSON.stringify(next)); } catch { /* no storage */ }
      setCollapsed(next);
    },
    // item 604 — drag lifecycle + persisted reorder
    draggingId,
    dropTarget,
    beginDrag: (id) => setDraggingId(id),
    hoverCard: (id, before) => setDropTarget(
      (prev) => (prev && prev.id === id && prev.before === before ? prev : { id, before }),
    ),
    endDrag: () => { setDraggingId(null); setDropTarget(null); },
    reorder: (draggedId, targetId, before) => {
      if (draggedId === targetId) return;
      const from = order.indexOf(draggedId);
      const targetIdx = order.indexOf(targetId);
      if (from < 0 || targetIdx < 0) return;
      const next = order.filter((k) => k !== draggedId);
      let insertAt = next.indexOf(targetId);
      if (!before) insertAt += 1;
      next.splice(insertAt, 0, draggedId);
      try { window.localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* no storage */ }
      setSaved(next);
    },
  }), [order, storageKey, collapsed, collapseKey, knownGroups, draggingId, dropTarget, autoCollapse]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

OrderableProvider.defaultProps = {
  groups: undefined,
  autoCollapse: false,
  defaultCollapsed: undefined,
};

const GroupHeader: React.FC<{
  title: string; collapsed: boolean; onToggle: () => void; caughtUp?: boolean; count?: number;
  owner?: string; // item 1048 (Chad) — ACCOUNTANT / ADMIN / SHARED ownership chip
}> = ({
  title, collapsed, onToggle, caughtUp, count, owner,
}) => {
  const border = useColorModeValue('blackAlpha.200', 'whiteAlpha.200');
  const check = useColorModeValue('green.400', 'green.300');
  const countBg = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const countColor = useColorModeValue('gray.700', 'whiteAlpha.900');
  const ownerColor = useColorModeValue('gray.500', 'whiteAlpha.600');
  const outstanding = count ?? 0;
  return (
    <Flex
      as="button"
      type="button"
      onClick={onToggle}
      align="center"
      sx={{ gap: '8px' }}
      w="100%"
      mb={3}
      pb={2}
      borderBottomWidth="2px"
      borderBottomColor={border}
      textAlign="left"
    >
      <Box as={collapsed ? FiChevronRight : FiChevronDown} fontSize="18px" />
      <Heading size="md" letterSpacing="-0.01em">{title}</Heading>
      {/* item 1048 (Chad) — whose desk this is, spelled out right on the header */}
      {owner && (
        <Box
          borderWidth="1px"
          borderColor={ownerColor}
          borderRadius="md"
          px={1.5}
          py={0.5}
          fontSize="0.6rem"
          fontWeight="bold"
          letterSpacing="0.08em"
          textTransform="uppercase"
          color={ownerColor}
          flexShrink={0}
        >
          {owner}
        </Box>
      )}
      {/* item 481 — a compact green check crowns a group once everything under it is cleared (Chad) */}
      {caughtUp && (
        <Flex
          w="22px"
          h="22px"
          ml={1}
          align="center"
          justify="center"
          borderRadius="full"
          bg={check}
          color="white"
          flexShrink={0}
          aria-label="All caught up"
        >
          <Box as={FaCheck} fontSize="11px" />
        </Flex>
      )}
      {/* item 481 — until it's cleared, the header shows how many items are outstanding (Chad) */}
      {!caughtUp && outstanding > 0 && (
        <Flex
          ml={1}
          px={2.5}
          h="22px"
          minW="22px"
          align="center"
          justify="center"
          borderRadius="full"
          bg={countBg}
          color={countColor}
          fontSize="sm"
          fontWeight="bold"
          flexShrink={0}
          aria-label={`${outstanding} outstanding`}
        >
          {outstanding}
        </Flex>
      )}
    </Flex>
  );
};
GroupHeader.defaultProps = { caughtUp: false, count: 0, owner: undefined };

export const Orderable: React.FC<{
  id: string; label: string; group?: string; groupTitle?: string; hidden?: boolean;
  groupCaughtUp?: boolean; groupCount?: number; children: React.ReactNode;
  groupOwner?: string; // item 1048 — ACCOUNTANT / ADMIN / SHARED chip on the group header
}> = ({
  id, label, group, groupTitle, hidden, groupCaughtUp, groupCount, children, groupOwner,
}) => {
  const ctx = useContext(Ctx);
  // item 604 — `armed` gates the native `draggable` attribute onto the WHOLE card, but only
  // after the grip handle is pressed. So a plain mousedown in the card body never starts a
  // drag (text selection / scroll / clicks work); pressing the handle arms the card so the
  // drag payload is the entire card. Reset on mouseup (a click that didn't drag) and dragend.
  const [armed, setArmed] = useState(false);
  const indicatorColor = useColorModeValue('brandGreen.500', 'brandGreen.300');
  // item 481/1050 — a superseded section stays hidden even when no provider wraps it
  // (the accountant page dropped its OrderableProvider for the scoreboard+tabs layout)
  if (hidden) return null;
  if (!ctx) return <>{children}</>;
  const pos = ctx.posOf(id);
  const collapsed = group ? ctx.isCollapsed(group) : false;
  const isDragging = ctx.draggingId === id;
  const isTarget = ctx.dropTarget?.id === id && ctx.draggingId !== null && ctx.draggingId !== id;
  const showBefore = isTarget && ctx.dropTarget?.before === true;
  const showAfter = isTarget && ctx.dropTarget?.before === false;

  const onDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch { /* some browsers restrict setData */ }
    ctx.beginDrag(id);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (!ctx.draggingId || ctx.draggingId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    ctx.hoverCard(id, e.clientY - rect.top < rect.height / 2);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const dragged = ctx.draggingId;
    if (dragged && dragged !== id && ctx.dropTarget) ctx.reorder(dragged, id, ctx.dropTarget.before);
    ctx.endDrag();
    setArmed(false);
  };
  const onDragEnd = () => { ctx.endDrag(); setArmed(false); };

  return (
    <Box
      id={id}
      style={{ order: pos }}
      role="group"
      scrollMarginTop="90px"
      position="relative"
      draggable={armed && !collapsed}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      opacity={isDragging ? 0.4 : 1}
      transition="opacity 0.12s"
    >
      {/* item 604 — the drop-indicator: a bright line on the edge the card will land against */}
      {showBefore && <Box position="absolute" top="-4px" left={0} right={0} h="3px" borderRadius="full" bg={indicatorColor} zIndex={2} />}
      {showAfter && <Box position="absolute" bottom="-4px" left={0} right={0} h="3px" borderRadius="full" bg={indicatorColor} zIndex={2} />}
      {/* item 478 — the first section of a group renders its collapse header (always visible) */}
      {groupTitle && group && (
        <GroupHeader title={groupTitle} collapsed={collapsed} onToggle={() => ctx.toggleGroup(group)} caughtUp={groupCaughtUp} count={groupCount} owner={groupOwner} />
      )}
      {/* the section body + grip handle hide when the group is collapsed */}
      <Box display={collapsed ? 'none' : undefined}>
        {/* item 604 — the grab bar: quiet until the section is hovered, then a grip handle */}
        <Flex
          justify="flex-end"
          align="center"
          sx={{ gap: '6px' }}
          mb={0.5}
          opacity={0.35}
          transition="opacity 0.15s"
          _groupHover={{ opacity: 1 }}
        >
          <Text fontSize="0.62rem" letterSpacing="0.08em" opacity={0.8} textTransform="uppercase">
            {label}
          </Text>
          <Box
            as="button"
            type="button"
            aria-label={`Drag to reorder ${label}`}
            title="Drag to reorder"
            onMouseDown={() => setArmed(true)}
            onMouseUp={() => setArmed(false)}
            display="flex"
            alignItems="center"
            justifyContent="center"
            px={1.5}
            py={1}
            borderRadius="md"
            cursor={armed ? 'grabbing' : 'grab'}
            _hover={{ bg: GRIP_HOVER_BG }}
          >
            <Box as={FiMove} fontSize="13px" />
          </Box>
        </Flex>
        {children}
      </Box>
    </Box>
  );
};

Orderable.defaultProps = {
  group: undefined, groupTitle: undefined, hidden: false, groupCaughtUp: undefined, groupCount: 0, groupOwner: undefined,
};
